// Every fixed label a full report prints (the DOCX in apps/app/src/exports/dossier-docx.ts, the HTML printed to PDF in
// dossier-html.ts, the tables and notes built in dossier.ts and the export route), in English and in French. One
// dictionary, so both renderers say the same thing in the language chosen at export. What the records say (names,
// claim text, quotes, sources, the schema's field names) is data and stays as written.

import { CLAIM_LINK_LABELS, type ClaimLinkKind } from './claim-links';
import type { AttributionLevel, ClaimKind, ReviewStatus } from './claims';
import type { EvidenceLevel } from './evidence';
import { RELIABILITY_LABELS, RELIABILITY_MEANINGS, type ReliabilityStatus } from './reliability';
import type { ReportLanguage } from './report-language';
import { REPORT_TABLE_WORDS, type ReportTableWords } from './reports';
import type { EntityKind } from './schema/types';

/** Singular or plural, by count. */
const count = (n: number, one: string, many: string) => (n === 1 ? one : many);

export interface DossierLabels {
  /** BCP 47 tag for `<html lang>` and number formatting. */
  locale: string;
  fullReport: string;
  exportedFromLiveData: string;
  /** DOCX metadata description, before the watermark. */
  exportDescription: string;
  /** Title page: "by <owner>" · "created <date>" · "language <name>". */
  by: (name: string) => string;
  created: (date: string) => string;
  language: string;
  /** The names of the document languages, in this language. */
  languageNames: Record<ReportLanguage, string>;
  signedOffBy: (name: string) => string;
  signedOff: string;
  draftStatus: string;
  noFindings: string;
  caveatsTitle: string;
  contents: string;
  executiveSummary: string;
  /** The one chapter of a report without template sections. */
  results: string;
  sources: string;
  evidenceLevels: string;
  annexCorrections: string;
  annexBibliography: string;
  annexGlossary: string;
  /** The closing block of the DOCX (what is withheld, the watermark). */
  closingNotes: string;
  noSectionText: string;
  noMatches: string;
  noNotes: string;
  evidence: (level: EvidenceLevel) => string;
  source: string;
  noneRecorded: string;
  status: Record<ReviewStatus, string>;
  claimKind: Record<ClaimKind, string>;
  attribution: Record<AttributionLevel, string>;
  legendHeaders: [string, string];
  evidenceMeanings: Record<EvidenceLevel, string>;
  /** Official document / Corroborated / Single source / Hypothesis, and their legend after the evidence levels. */
  reliability: Record<ReliabilityStatus, string>;
  reliabilityTitle: string;
  reliabilityHeaders: [string, string];
  reliabilityMeanings: Record<ReliabilityStatus, string>;
  /** A note's response and rebuttals: "Response", "Contradicted by", read from the cited claim's side or the other's. */
  claimLink: Record<ClaimLinkKind, { out: string; in: string }>;
  correctionsHeaders: [string, string, string, string, string];
  noCorrections: string;
  bibliographyHeaders: [string, string, string, string, string];
  noBibliography: string;
  /** The bibliography entries the analyst added by hand (Annex B, after the cited sources). */
  addedByAnalyst: string;
  glossaryHeaders: [string, string];
  noGlossary: string;
  withheld: string;
  figure: (n: number) => string;
  /** "Table 4." before a table's title, and "data: table 4" in a figure's caption. */
  table: (n: number) => string;
  dataTable: (n: number) => string;
  /** Headers of the data tables of the typed figures. */
  figureData: {
    tiles: [string, string];
    schematic: [string, string, string, string];
    statusTimeline: [string, string, string];
    votesPerson: string;
    beforeAfterItem: string;
    beforeAfterRatio: string;
    /** The data tables of a timeline (lane, item, from, to) and of clusters (cluster, members, most linked). */
    timeline: [string, string, string, string];
    clusters: [string, string, string];
    /** "Hoopoe records" / "Données Hoopoe": the source line of a figure drawn from the records. */
    records: string;
  };
  /** Under a results table that prints only its first rows (D384). */
  tableNote: (shown: number, total: number) => string;
  centralityTitle: string;
  centralityHeaders: [string, string, string, string];
  centralityNote: (shown: number, total: number) => string;
  /** The results tables' column headers and fixed words (the schema's field and type labels are data, D416). */
  resultTable: ReportTableWords;
  /** A results table grouped by a field: the sub-heading of one value ("Level: Strategic"), and of the rows without one. */
  group: (field: string, value: string) => string;
  notSet: string;
  /** The money links among a section's rows, after its table. */
  flowsTitle: string;
  flowsHeaders: [string, string, string, string, string, string];
  noFlows: string;
  flowsNote: (shown: number, total: number) => string;
  undisclosed: string;
  /** Under a section table limited to the report's question (D395): the same kind, or a kind linked to it. */
  withinSame: (words: string) => string;
  withinLinked: (kind: EntityKind, words: string) => string;
  /** The kinds of entity a report lists, plural and lower-case, for `withinLinked`. */
  kinds: Record<EntityKind, string>;
  /** The kinds of source in the bibliography, by the English word the claims service gives. */
  sourceKinds: Record<string, string>;
  caveats: {
    allegation: (n: number) => string;
    unreviewed: (n: number) => string;
    disputed: (n: number) => string;
    unsupported: (n: number) => string;
    confidential: string;
    fallback: (route: string) => string;
    /** Some sections (`count` of `total`) were drafted by a fallback route after the usual one gave no usable answer. */
    fallbackSome: (route: string, count: number, total: number) => string;
  };
}

const num = (locale: string) => (n: number) => n.toLocaleString(locale);

const EN_NUM = num('en');
const FR_NUM = num('fr-FR');

export const DOSSIER_LABELS: Record<ReportLanguage, DossierLabels> = {
  en: {
    locale: 'en',
    fullReport: 'Full report',
    exportedFromLiveData: 'exported from live data',
    exportDescription: 'Full report export',
    by: name => `by ${name}`,
    created: date => `created ${date}`,
    language: 'language',
    languageNames: { en: 'English', fr: 'French' },
    signedOffBy: name => `Signed off by ${name}`,
    signedOff: 'Signed off',
    draftStatus: 'Draft: findings not signed off',
    noFindings: 'No AI findings yet',
    caveatsTitle: 'Before you rely on this report',
    contents: 'Contents',
    executiveSummary: 'Executive summary',
    results: 'Results',
    sources: 'Sources',
    evidenceLevels: 'Evidence levels',
    annexCorrections: 'Annex A — Corrections',
    annexBibliography: 'Annex B — Bibliography',
    annexGlossary: 'Annex C — Glossary',
    closingNotes: 'Notes',
    noSectionText: 'No findings text for this section.',
    noMatches: 'No matches.',
    noNotes: 'The findings cite no claims.',
    evidence: level => `Evidence ${level}`,
    source: 'Source:',
    noneRecorded: 'none recorded',
    status: { unreviewed: 'Unreviewed', verified: 'Verified', rejected: 'Rejected', disputed: 'Disputed' },
    claimKind: { fact: 'Fact', statement: 'Statement', assessment: 'Assessment', allegation: 'Allegation' },
    attribution: { on_record: 'On record', background: 'Background', off_record: 'Off record', anonymous: 'Anonymous source' },
    legendHeaders: ['Level', 'Meaning'],
    reliability: RELIABILITY_LABELS.en,
    reliabilityTitle: 'Reliability status',
    reliabilityHeaders: ['Status', 'Meaning'],
    reliabilityMeanings: RELIABILITY_MEANINGS.en,
    claimLink: CLAIM_LINK_LABELS.en,
    evidenceMeanings: {
      A: 'Verified and corroborated: publishable as is.',
      B: 'Verified from one solid source, or two good secondary ones: publishable with attribution.',
      C: 'One secondary source or partial data: corroborate before publishing.',
      D: 'Unverified, anonymous or speculative: internal use only.',
    },
    correctionsHeaders: ['Entity', 'Claim', 'Status', 'Reason', 'Source'],
    noCorrections: 'No claims about these entities were rejected or disputed.',
    bibliographyHeaders: ['Source', 'Kind', 'Date', 'URL', 'Cited claims'],
    noBibliography: 'No sources: the report cites no claims yet.',
    addedByAnalyst: 'Added by the analyst',
    glossaryHeaders: ['Term', 'Meaning'],
    noGlossary: 'No acronyms to explain.',
    withheld: 'Off-record claims are left out of reports and their exports.',
    figure: n => `Figure ${n}.`,
    table: n => `Table ${n}.`,
    dataTable: n => `data: table ${n}`,
    figureData: {
      tiles: ['Key figure', 'Value'],
      schematic: ['From', 'Link', 'To', 'Note'],
      statusTimeline: ['Date', 'Event', 'Status'],
      votesPerson: 'Person',
      beforeAfterItem: 'Item',
      beforeAfterRatio: 'Ratio',
      timeline: ['Entity', 'Item', 'From', 'To'],
      clusters: ['Cluster', 'Members', 'Most linked'],
      records: 'Hoopoe records',
    },
    tableNote: (shown, total) =>
      `Showing the first ${shown} of ${EN_NUM(total)} matches. The full list is in the report’s CSV or Excel export.`,
    centralityTitle: 'Most central actors',
    resultTable: REPORT_TABLE_WORDS,
    centralityHeaders: ['Rank', 'Entity', 'Links', 'Betweenness'],
    centralityNote: (shown, total) => `Showing the ${shown} most central of ${EN_NUM(total)} entities.`,
    group: (field, value) => `${field}: ${value}`,
    notSet: 'Not set',
    flowsTitle: 'Money flows among the results',
    flowsHeaders: ['Payer', 'Recipient', 'Amount', 'Currency', 'Period', 'Source'],
    noFlows: 'No money links among the results.',
    flowsNote: (shown, total) => `Showing the ${shown} largest of ${EN_NUM(total)} money links.`,
    undisclosed: 'undisclosed',
    withinSame: words => `Limited to the report’s question: ${words}.`,
    withinLinked: (kind, words) => `Limited to ${DOSSIER_LABELS.en.kinds[kind]} linked to the report’s question: ${words}.`,
    kinds: { actor: 'actors', event: 'events', location: 'places', asset: 'assets / transactions', document: 'sources', case: 'cases' },
    sourceKinds: {},
    caveats: {
      allegation: n => `Cites ${n} ${count(n, 'allegation', 'allegations')}: what a source says happened, not verified.`,
      unreviewed: n => `Rests partly on unreviewed data: ${n} ${count(n, 'cited claim', 'cited claims')} not yet checked by a reviewer.`,
      disputed: n => `Includes disputed data: sources disagree on ${n} ${count(n, 'cited claim', 'cited claims')}.`,
      unsupported: n =>
        `${n} ${count(n, 'sentence', 'sentences')} ${count(n, 'cites', 'cite')} no claim and ${count(n, 'is', 'are')} marked. Add a source or rephrase before signing off.`,
      confidential:
        'Confidential material was involved, so a Cloudflare-native model wrote what draws on it and nothing confidential left Cloudflare.',
      fallback: route => `Drafted by ${route} because the usual AI route was unavailable.`,
      fallbackSome: (route, count, total) =>
        `${count} of ${total} sections ${count === 1 ? 'was' : 'were'} drafted by ${route} because the usual AI route gave no usable answer.`,
    },
  },
  fr: {
    locale: 'fr',
    fullReport: 'Rapport complet',
    exportedFromLiveData: 'exporté à partir des données en direct',
    exportDescription: 'Export du rapport complet',
    by: name => `par ${name}`,
    created: date => `créé le ${date}`,
    language: 'langue',
    languageNames: { en: 'anglais', fr: 'français' },
    signedOffBy: name => `Validé par ${name}`,
    signedOff: 'Validé',
    draftStatus: 'Brouillon : constats non validés',
    noFindings: 'Pas encore de constats rédigés par l’IA',
    caveatsTitle: 'Avant de vous fier à ce rapport',
    contents: 'Sommaire',
    executiveSummary: 'Synthèse',
    results: 'Résultats',
    sources: 'Sources',
    evidenceLevels: 'Niveaux de preuve',
    annexCorrections: 'Annexe A — Corrections',
    annexBibliography: 'Annexe B — Bibliographie',
    annexGlossary: 'Annexe C — Glossaire',
    closingNotes: 'Notes',
    noSectionText: 'Aucun texte de constats pour cette section.',
    noMatches: 'Aucun résultat.',
    noNotes: 'Les constats ne citent aucune affirmation.',
    evidence: level => `Preuve ${level}`,
    source: 'Source :',
    noneRecorded: 'aucune enregistrée',
    status: { unreviewed: 'Non revue', verified: 'Vérifiée', rejected: 'Rejetée', disputed: 'Contestée' },
    claimKind: { fact: 'Fait', statement: 'Déclaration', assessment: 'Évaluation', allegation: 'Allégation' },
    attribution: {
      on_record: 'Attribuable',
      background: 'Contexte (non attribuable)',
      off_record: 'Off the record',
      anonymous: 'Source anonyme',
    },
    legendHeaders: ['Niveau', 'Signification'],
    reliability: RELIABILITY_LABELS.fr,
    reliabilityTitle: 'Statut de fiabilité',
    reliabilityHeaders: ['Statut', 'Signification'],
    reliabilityMeanings: RELIABILITY_MEANINGS.fr,
    claimLink: CLAIM_LINK_LABELS.fr,
    evidenceMeanings: {
      A: 'Vérifié et corroboré : publiable tel quel.',
      B: 'Vérifié par une source solide, ou par deux bonnes sources secondaires : publiable avec attribution.',
      C: 'Une source secondaire ou des données partielles : à corroborer avant publication.',
      D: 'Non vérifié, anonyme ou spéculatif : usage interne uniquement.',
    },
    correctionsHeaders: ['Entité', 'Affirmation', 'Statut', 'Motif', 'Source'],
    noCorrections: 'Aucune affirmation sur ces entités n’a été rejetée ni contestée.',
    bibliographyHeaders: ['Source', 'Type', 'Date', 'URL', 'Affirmations citées'],
    noBibliography: 'Aucune source : le rapport ne cite encore aucune affirmation.',
    addedByAnalyst: 'Ajouté par l’analyste',
    glossaryHeaders: ['Terme', 'Signification'],
    noGlossary: 'Aucun sigle à expliquer.',
    withheld: 'Les affirmations « off the record » sont exclues des rapports et de leurs exports.',
    figure: n => `Figure ${n}.`,
    table: n => `Tableau ${n}.`,
    dataTable: n => `données : tableau ${n}`,
    figureData: {
      tiles: ['Chiffre clé', 'Valeur'],
      schematic: ['De', 'Lien', 'Vers', 'Note'],
      statusTimeline: ['Date', 'Événement', 'Statut'],
      votesPerson: 'Personne',
      beforeAfterItem: 'Élément',
      beforeAfterRatio: 'Rapport',
      timeline: ['Entité', 'Élément', 'Du', 'Au'],
      clusters: ['Groupe', 'Membres', 'Les plus liés'],
      records: 'Données Hoopoe',
    },
    tableNote: (shown, total) =>
      `${count(shown, 'Seul le premier', `Seuls les ${shown} premiers`)} des ${FR_NUM(total)} résultats ${count(shown, 'est affiché', 'sont affichés')}. La liste complète figure dans l’export CSV ou Excel du rapport.`,
    centralityTitle: 'Acteurs les plus centraux',
    resultTable: {
      headers: {
        name: 'Nom',
        code: 'Code',
        type: 'Type',
        links: 'Liens correspondants',
        reviewStatus: 'Statut de revue',
        sensitivity: 'Sensibilité',
        updated: 'Mis à jour',
      },
      status: { unreviewed: 'Non revue', verified: 'Vérifiée', rejected: 'Rejetée', disputed: 'Contestée' },
      sensitivity: { standard: 'standard', confidential: 'confidentiel' },
      reliability: RELIABILITY_LABELS.fr,
    },
    centralityHeaders: ['Rang', 'Entité', 'Liens', 'Intermédiarité'],
    centralityNote: (shown, total) => `Les ${shown} entités les plus centrales sur ${FR_NUM(total)}.`,
    group: (field, value) => `${field} : ${value}`,
    notSet: 'Non renseigné',
    flowsTitle: 'Flux financiers entre les résultats',
    flowsHeaders: ['Payeur', 'Bénéficiaire', 'Montant', 'Devise', 'Période', 'Source'],
    noFlows: 'Aucun flux financier entre les résultats.',
    flowsNote: (shown, total) => `Les ${shown} flux les plus importants sur ${FR_NUM(total)}.`,
    undisclosed: 'non divulgué',
    withinSame: words => `Limité à la question du rapport : ${words}.`,
    withinLinked: (kind, words) => `Limité aux ${DOSSIER_LABELS.fr.kinds[kind]} liés à la question du rapport : ${words}.`,
    kinds: {
      actor: 'acteurs',
      event: 'événements',
      location: 'lieux',
      asset: 'actifs / transactions',
      document: 'sources',
      case: 'dossiers',
    },
    sourceKinds: {
      'Web page': 'Page web',
      'Social media post': 'Publication sur les réseaux sociaux',
      'Interview transcript': 'Transcription d’entretien',
      Interview: 'Entretien',
      'Cited source': 'Source citée',
      'Profile sheet': 'Fiche profil',
      'Actor list': 'Liste d’acteurs',
      Transcript: 'Transcription',
      'Interview synthesis': 'Synthèse d’entretien',
      'Report / note': 'Rapport / note',
      'Guide note': 'Note d’orientation',
      Dataset: 'Jeu de données',
      'Audio / video': 'Audio / vidéo',
      'Image / scan': 'Image / scan',
      Document: 'Document',
    },
    caveats: {
      allegation: n => `Cite ${n} ${count(n, 'allégation', 'allégations')} : ce qu’une source affirme s’être produit, sans vérification.`,
      unreviewed: n =>
        `Repose en partie sur des données non revues : ${n} ${count(n, 'affirmation citée n’a', 'affirmations citées n’ont')} pas encore été ${count(n, 'vérifiée', 'vérifiées')} par un relecteur.`,
      disputed: n =>
        `Comprend des données contestées : les sources divergent sur ${n} ${count(n, 'affirmation citée', 'affirmations citées')}.`,
      unsupported: n =>
        `${n} ${count(n, 'phrase ne cite', 'phrases ne citent')} aucune affirmation et ${count(n, 'est signalée', 'sont signalées')}. Ajoutez une source ou reformulez avant de valider.`,
      confidential:
        'Des éléments confidentiels étaient en jeu : un modèle natif de Cloudflare a rédigé ce qui s’appuie sur eux, et rien de confidentiel n’a quitté Cloudflare.',
      fallback: route => `Rédigé par ${route}, car la voie IA habituelle n’était pas disponible.`,
      fallbackSome: (route, count, total) =>
        `${count} section${count === 1 ? '' : 's'} sur ${total} ${count === 1 ? 'a été rédigée' : 'ont été rédigées'} par ${route}, car la voie IA habituelle n’a pas donné de réponse utilisable.`,
    },
  },
};

/** The labels of a full report in this language (English when the language is unknown). */
export const dossierLabels = (lang: ReportLanguage | null | undefined): DossierLabels => DOSSIER_LABELS[lang ?? 'en'] ?? DOSSIER_LABELS.en;

/** A source kind from the claims service ("Web page"), in the document's language (unknown kinds stay as given). */
export const sourceKindLabel = (kind: string, l: DossierLabels) => l.sourceKinds[kind] ?? kind;
